import {cn} from '@/lib/utils'
import React, {useEffect, useRef, useState} from 'react'
import {useOnClickOutside} from '../../hooks/useOnClickOutsideHook'
import useMediaQuery from '../../hooks/useWindowSize'
import {IWuAppHeaderMenuItem} from '../../types/WuAppHeaderMenuItem'
import {createHandleKeyDown} from '../../utils/headerMenuKeyboardHandlers'
import {MenuTrigger} from './ui/_menuTrigger'
import {ProductDropdownMenu} from './ui/_productDropdown'
import {SmallScreenMenuTrigger} from './ui/_smallScreenMenuTrigger'
import {SmallScreenToggleMenu} from './ui/_smallScreenToggleMenu'
import './WuAppHeaderMenu.css'

interface IWuAppHeaderMenuProps {
  productCategories: IWuAppHeaderMenuItem[]
  activeProductName: string
  activeProductColor?: {
    base: string
    hover: string
  }
  dir: 'ltr' | 'rtl'
  homeLink?: string
  expandAll?: boolean
}

export const WuAppHeaderMenu: React.FC<IWuAppHeaderMenuProps> = ({
  productCategories,
  activeProductName,
  activeProductColor = {base: '#1b87e6', hover: '#54A5EC'},
  dir,
  homeLink,
  expandAll,
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const dropdownRef = useRef<HTMLUListElement | null>(null)
  const smallScreenButtonRef = useRef<HTMLButtonElement | null>(null)
  const smallScreenToggleButtonRef = useRef<HTMLButtonElement | null>(null)

  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const [openCategory, setOpenCategory] = useState<IWuAppHeaderMenuItem | null>(
    null,
  )
  const [activeProductBgColor, setActiveProductBgColor] = useState(
    activeProductColor.base,
  )
  const [focusItem, setFocusItem] = useState<string>('')
  const [isProductButtonShow, setIsProductButtonShow] = useState<boolean>(false)

  const isMd = useMediaQuery(768)

  useOnClickOutside([dropdownRef, buttonRef], () => {
    setDropdownOpen(false)
    setOpenCategory(null)
  })

  useOnClickOutside(
    [smallScreenButtonRef, smallScreenToggleButtonRef, dropdownRef],
    () => {
      setIsProductButtonShow(false)
      setDropdownOpen(false)
      setOpenCategory(null)
    },
  )

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscKey)
    return (): void => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [isDropdownOpen])

  useEffect(
    () => setActiveProductBgColor(activeProductColor.base),
    [activeProductColor.base],
  )

  const handleDropdownToggle: React.MouseEventHandler = event => {
    event.preventDefault()
    event.stopPropagation()
    setDropdownOpen(prev => {
      const nextState = !prev
      if (!nextState) {
        setOpenCategory(null)
      }
      return nextState
    })
  }

  const handleKeyDown = (event: React.KeyboardEvent, index: number): void => {
    const handler = createHandleKeyDown({
      productCategories,
      openCategory,
      setOpenCategory,
    })
    handler(event, index, focusItem)
  }

  return (
    <div
      className={cn(
        'inline-flex items-center justify-between  relative z-auto h-12',
      )}
    >
      {!isMd ? (
        <div
          className="relative flex items-center justify-start h-full w-[256px]"
          dir={dir}
          style={{
            backgroundColor: isDropdownOpen
              ? activeProductColor.hover
              : activeProductBgColor,
          }}
          onMouseOver={() => setActiveProductBgColor(activeProductColor.hover)}
          onMouseOut={() => setActiveProductBgColor(activeProductColor.base)}
        >
          <MenuTrigger
            activeProductName={activeProductName}
            dir={dir}
            homeLink={homeLink}
            buttonRef={buttonRef}
            handleDropdownToggle={handleDropdownToggle}
            handleKeyDown={handleKeyDown}
            isDropdownOpen={isDropdownOpen}
          />
          <ProductDropdownMenu
            activeProductName={activeProductName}
            focusItem={focusItem}
            setFocusItem={setFocusItem}
            handleKeyDown={handleKeyDown}
            isDropdownOpen={isDropdownOpen}
            openCategory={openCategory}
            productCategories={productCategories}
            setOpenCategory={setOpenCategory}
            dropdownRef={dropdownRef}
            expandAll={expandAll}
            dir={dir}
          />
        </div>
      ) : (
        <div className="relative flex">
          <SmallScreenToggleMenu
            dir={dir}
            isProductButtonShow={isProductButtonShow}
            smallScreenToggleButtonRef={smallScreenToggleButtonRef}
            setDropdownOpen={setDropdownOpen}
            setIsProductButtonShow={setIsProductButtonShow}
          />
          <SmallScreenMenuTrigger
            isProductButtonShow={isProductButtonShow}
            isDropdownOpen={isDropdownOpen}
            activeProductColor={activeProductColor}
            activeProductBgColor={activeProductBgColor}
            activeProductName={activeProductName}
            handleDropdownToggle={handleDropdownToggle}
            smallScreenButtonRef={smallScreenButtonRef}
            handleKeyDown={handleKeyDown}
            dir={dir}
          />
          <div
            className={cn(
              'absolute top-[92px] left-0 text-[18px]',
              dir === 'rtl' && 'right-[256px] left-auto',
            )}
          >
            <ProductDropdownMenu
              activeProductName={activeProductName}
              focusItem={focusItem}
              setFocusItem={setFocusItem}
              handleKeyDown={handleKeyDown}
              isDropdownOpen={isDropdownOpen}
              openCategory={openCategory}
              productCategories={productCategories}
              setOpenCategory={setOpenCategory}
              dropdownRef={dropdownRef}
              expandAll={expandAll}
              dir={dir}
            />
          </div>
        </div>
      )}
    </div>
  )
}
